<template>
  <div v-if="showSyncBar" class="pl-sync" role="status" aria-live="polite">
    <div class="pl-sync__row">
      <span class="pl-sync__icon"><PlIcon name="refresh" :size="16" /></span>
      <div class="pl-sync__text">
        <p class="pl-sync__title">Synchronizacja ze Stravą</p>
        <p class="pl-sync__message">{{ syncMessage }}</p>
      </div>
    </div>
    <div class="pl-sync__track">
      <div class="pl-sync__fill" :style="{ width: progressPercent + '%' }"></div>
    </div>
    <div class="pl-sync__stats">
      <span><strong>{{ animatedStats.peaks }}</strong> {{ plural(animatedStats.peaks, PEAK_FORMS) }}</span>
      <span><strong>{{ animatedStats.ascents }}</strong> {{ plural(animatedStats.ascents, ['wejście', 'wejścia', 'wejść']) }}</span>
      <span><strong>{{ animatedStats.activities }}</strong> {{ plural(animatedStats.activities, ['aktywność', 'aktywności', 'aktywności']) }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import api from '@/services/api'
import PlIcon from '@/components/pl/PlIcon.vue'
import { plural, PEAK_FORMS } from '@/utils/format'

const authStore = useAuthStore()
const dataStore = useDataStore()

const showSyncBar = computed(() => {
  const authenticated = authStore.isAuthenticated
  const stravaConnected = authStore.isStravaConnected
  const stravaSynced = authStore.isStravaSynced
  const processingComplete = isProcessingComplete.value

  console.log('SyncStatusBar conditions:', {
    authenticated,
    stravaConnected,
    stravaSynced,
    processingComplete,
    showBar: authenticated && stravaConnected && (!stravaSynced || !processingComplete)
  })

  // Simplified condition: show bar if connected to Strava and not fully processed
  // (dopiero po pierwszym sprawdzeniu statusu, żeby pasek nie migał przy każdym wejściu)
  return authenticated && stravaConnected && syncChecked.value && !processingComplete
})

const syncMessage = ref('Wczytujemy Twoje aktywności…')
const progressPercent = ref(0)
const animatedStats = ref({
  peaks: 0,
  activities: 0,
  ascents: 0
})

const isProcessingComplete = ref(false)
const syncChecked = ref(false)

const syncInterval = ref(null)
const userCheckInterval = ref(null)
const animationTimeouts = ref([])

// Animation function for counting up
const animateValue = (startVal, endVal, duration, callback) => {
  const startTime = Date.now()
  const difference = endVal - startVal

  const animate = () => {
    const elapsed = Date.now() - startTime
    const progress = Math.min(elapsed / duration, 1)
    const currentValue = Math.floor(startVal + (difference * progress))

    callback(currentValue)

    if (progress < 1) {
      const timeout = setTimeout(animate, 16) // ~60fps
      animationTimeouts.value.push(timeout)
    }
  }

  animate()
}

// Update stats with animation
const updateStatsWithAnimation = (newStats) => {
  // Clear existing animation timeouts
  animationTimeouts.value.forEach(timeout => clearTimeout(timeout))
  animationTimeouts.value = []

  // Animate each stat
  animateValue(animatedStats.value.peaks, newStats.peaks, 1000, (value) => {
    animatedStats.value.peaks = value
  })

  animateValue(animatedStats.value.activities, newStats.activities, 1000, (value) => {
    animatedStats.value.activities = value
  })

  animateValue(animatedStats.value.ascents, newStats.ascents, 1000, (value) => {
    animatedStats.value.ascents = value
  })
}

// Load current data and animate
const loadAndAnimateStats = async () => {
  try {
    // Load fresh data from API (force refresh during sync)
    await dataStore.loadAscents(true)
    await dataStore.loadActivities(true)

    // Calculate current stats
    const currentStats = {
      peaks: dataStore.uniqueLocations,
      activities: dataStore.activities.length,
      ascents: dataStore.ascents.length
    }

    // Calculate progress based on ascents vs activities
    if (currentStats.activities > 0) {
      progressPercent.value = Math.min((currentStats.ascents / currentStats.activities) * 100, 95) // Max 95% until fully synced
    }

    // Animate to new values
    updateStatsWithAnimation(currentStats)

    // Update sync message based on progress
    if (currentStats.ascents > 0) {
      syncMessage.value = `Znaleziono ${currentStats.ascents} ${plural(currentStats.ascents, ['wejście', 'wejścia', 'wejść'])} na ${currentStats.peaks} ${plural(currentStats.peaks, PEAK_FORMS)}…`
    } else {
      syncMessage.value = 'Przeglądamy Twoje aktywności…'
    }

  } catch (error) {
    console.error('Error loading sync stats:', error)
    syncMessage.value = 'Problem z połączeniem, ponawiamy…'
  }
}

// Check if sync is complete
const checkSyncStatus = async () => {
  try {
    await authStore.fetchUser()

    // First check if Strava connection is synced (activities downloaded)
    if (!authStore.isStravaSynced) {
      console.log('SyncStatusBar: Strava not synced yet, showing bar')
      return // Still syncing activities from Strava
    }

    // Then check if all activities are processed (worker finished)
    console.log('SyncStatusBar: Checking processing status...')
    const syncStatusResponse = await api.get('/api/user/sync_strava')
    const syncStatus = syncStatusResponse.data
    console.log('SyncStatusBar: Sync status response:', syncStatus)

    if (syncStatus.is_fully_synced) {
      // Full sync completed, stop intervals
      console.log('SyncStatusBar: Processing complete, hiding bar')
      isProcessingComplete.value = true
      progressPercent.value = 100
      stopSyncMonitoring()
    } else {
      // Update message to show processing status
      console.log('SyncStatusBar: Still processing, showing bar')
      isProcessingComplete.value = false
      syncMessage.value = `Przetwarzamy ${syncStatus.unprocessed_activities} ${plural(syncStatus.unprocessed_activities, ['aktywność', 'aktywności', 'aktywności'])}…`
    }
  } catch (error) {
    console.error('Error checking sync status:', error)
    syncMessage.value = 'Problem z połączeniem, ponawiamy…'
  }
}

// Start monitoring sync progress
const startSyncMonitoring = () => {
  // Load initial data
  loadAndAnimateStats()

  // Set up periodic data loading (every 20 seconds)
  syncInterval.value = setInterval(loadAndAnimateStats, 20000)

  // Set up periodic sync status checking (every 30 seconds)
  userCheckInterval.value = setInterval(checkSyncStatus, 30000)
}

// Stop monitoring
const stopSyncMonitoring = () => {
  if (syncInterval.value) {
    clearInterval(syncInterval.value)
    syncInterval.value = null
  }

  if (userCheckInterval.value) {
    clearInterval(userCheckInterval.value)
    userCheckInterval.value = null
  }

  // Clear animation timeouts
  animationTimeouts.value.forEach(timeout => clearTimeout(timeout))
  animationTimeouts.value = []
}

// Watch for auth changes
watch(() => authStore.isAuthenticated, (newVal) => {
  if (newVal && authStore.isStravaConnected && !authStore.isStravaSynced) {
    startSyncMonitoring()
  } else {
    stopSyncMonitoring()
  }
})

watch(() => authStore.isStravaSynced, (newVal) => {
  if (newVal) {
    stopSyncMonitoring()
  }
})

onMounted(async () => {
  // Always check sync status on mount to ensure proper state
  await checkSyncStatus()
  syncChecked.value = true

  if (showSyncBar.value) {
    startSyncMonitoring()
  }
})

onUnmounted(() => {
  stopSyncMonitoring()
})
</script>

<style scoped>
.pl-sync {
  width: calc(100% - 2 * var(--pl-space-page-x));
  max-width: 440px;
  margin: 16px auto 0;
  padding: 14px 16px;
  border: 1px solid var(--pl-color-border-subtle);
  border-radius: var(--pl-radius-card);
  background: var(--pl-color-bg-sunken);
  color: var(--pl-color-text-primary);
  font: var(--pl-type-small);
}

@media (min-width: 1024px) {
  .pl-sync {
    width: calc(100% - 2 * var(--pl-space-desktop-x));
    max-width: calc(var(--pl-space-content-max) - 2 * var(--pl-space-desktop-x));
    margin-top: var(--pl-space-desktop-top);
  }
}

.pl-sync__row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pl-sync__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--pl-radius-pill);
  background: var(--pl-color-bg-card);
  color: var(--pl-color-accent-clay);
  animation: pl-sync-spin 2.4s linear infinite;
}

@keyframes pl-sync-spin {
  to {
    transform: rotate(360deg);
  }
}

.pl-sync__title {
  margin: 0;
  font: var(--pl-type-small-strong);
}

.pl-sync__message {
  margin: 2px 0 0;
  font: var(--pl-type-caption);
  color: var(--pl-color-text-secondary);
}

.pl-sync__track {
  height: 4px;
  margin-top: 12px;
  overflow: hidden;
  border-radius: var(--pl-radius-pill);
  background: var(--pl-color-bg-track);
}

.pl-sync__fill {
  height: 100%;
  border-radius: var(--pl-radius-pill);
  background: var(--pl-color-accent-clay);
  transition: width 0.5s ease;
}

.pl-sync__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 10px;
  font: var(--pl-type-caption);
  color: var(--pl-color-text-muted);
}

.pl-sync__stats strong {
  font: var(--pl-type-pill);
  font-variant-numeric: tabular-nums;
  color: var(--pl-color-text-primary);
}
</style>
